// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * An RFC 4180 CSV writer.
 *
 * Written rather than imported because every field in findings.csv is
 * third-party content — an author name, a comment, a paragraph of a contract —
 * and the four things that go wrong with such a field all go wrong quietly:
 *
 * 1. A LINE BREAK inside a quoted field is legal CSV and most tools read it
 *    correctly, but a person opening the file in an editor, or counting it with
 *    `wc -l`, sees a comment split across nine rows. Control characters become
 *    visible escapes so that ONE RECORD IS ALWAYS ONE PHYSICAL LINE.
 * 2. FORMULA INJECTION. A field beginning `=`, `+`, `-` or `@` is executed by
 *    Excel, LibreOffice and Google Sheets when the file is opened, and Word
 *    document text routinely begins with a hyphen (a bulleted line pasted as
 *    text). The guard is an apostrophe prefix AND forced quoting — unquoted,
 *    some importers strip the apostrophe again and restore the formula.
 * 3. THE SEPARATOR IS NOT UNIVERSAL. Excel in German, French and Spanish
 *    locales reads a comma-separated file as one column per row, so a field
 *    containing `;` is quoted too.
 * 4. NO BOM AND EXCEL MOJIBAKES the first non-ASCII character it meets, which
 *    for this tool means every author name with an accent in it.
 *
 * There is deliberately NO character-class regex for the control characters.
 * The obvious way to write one is to paste the characters, and a literal
 * control byte in a source file is invisible in review, in a diff and in a
 * terminal while silently changing what the regex matches — a source-hygiene
 * gate fails the build on one. The set is defined once, as a code-point test.
 */

/** True for C0 controls and DEL. Tab, CR and LF are included: they are escaped, not passed. */
export function isControl(code: number): boolean {
  return code < 0x20 || code === 0x7f;
}

function hasControl(value: string): boolean {
  for (let i = 0; i < value.length; i++) if (isControl(value.charCodeAt(i))) return true;
  return false;
}

const NAMED_ESCAPES = new Map<number, string>([
  [0x0d, '\\r'],
  [0x0a, '\\n'],
  [0x09, '\\t'],
]);

/** Replaces every control character with a visible escape. */
export function escapeControls(value: string): string {
  let out = '';
  for (const ch of value) {
    const code = ch.codePointAt(0) as number;
    const named = NAMED_ESCAPES.get(code);
    if (named !== undefined) out += named;
    else if (isControl(code)) out += `\\x${code.toString(16).padStart(2, '0')}`;
    else out += ch;
  }
  return out;
}

export function csvField(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  let text = escapeControls(String(value));

  // The injection guard runs on the ESCAPED text: a field that began with a tab
  // followed by `=` is still a formula to Excel, and after escaping the tab is
  // two visible characters rather than one invisible one.
  const dangerous = /^[=+\-@]/.test(text);
  if (dangerous) text = `'${text}`;

  const needsQuotes = dangerous || /[",;]/.test(text) || hasControl(text) || text !== text.trim();
  if (!needsQuotes) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

export function csvRow(cells: (string | number | boolean | null | undefined)[]): string {
  return cells.map(csvField).join(',');
}

/**
 * A complete CSV document.
 *
 * CRLF because RFC 4180 says so and because Excel on Windows is the single most
 * likely destination for this file. The BOM is added by `csvBlob` rather than
 * here, so the string form stays comparable in a test without one.
 */
export function csvText(header: string[], rows: (string | number | boolean | null | undefined)[][]): string {
  return [csvRow(header), ...rows.map(csvRow)].join('\r\n') + '\r\n';
}

export function csvBlob(header: string[], rows: (string | number | boolean | null | undefined)[][]): Blob {
  return new Blob([String.fromCharCode(0xfeff), csvText(header, rows)], { type: 'text/csv;charset=utf-8' });
}
